import { useCallback, useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  FILTER_CATEGORY_KEY,
  FILTER_OPTION_KEY,
  FilterCategory,
  FilterOption,
  MultilevelFiltersProps,
} from './multilevel-filters-types';

export const useMultilevelFilters = ({
  filterCategories,
}: MultilevelFiltersProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const filterOptionsInitialState = filterCategories?.[0].filterOptions || [];
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>(
    filterOptionsInitialState
  );

  const parsedSearchParams = useMemo(
    () => new URLSearchParams(searchParams.toString()),
    [searchParams]
  );

  const pushUpdatedFilterParams = useCallback(() => {
    window.history.pushState(
      {},
      '',
      `${pathname}?${parsedSearchParams.toString()}`
    );
  }, [parsedSearchParams, pathname]);

  // apply filter category and filter option from URL and reset filter options
  const handleFilterCategory = useCallback(
    (filterCategory: FilterCategory) => {
      const filterOptionsItems = filterCategory?.filterOptions;
      const newFilterOptionsValue = filterOptionsItems || [];

      setFilterOptions(newFilterOptionsValue);

      if (filterCategory.categoryValue) {
        parsedSearchParams.delete(FILTER_OPTION_KEY);
        parsedSearchParams.set(
          FILTER_CATEGORY_KEY,
          filterCategory.categoryValue
        );
        pushUpdatedFilterParams();
      } else {
        window.history.pushState({}, '', pathname);
      }
    },
    [parsedSearchParams, pathname, pushUpdatedFilterParams]
  );

  // apply filter option from URL
  const handleFilterOptionClick = useCallback(
    (filterOption: FilterOption) => {
      const filterAlreadyActive = parsedSearchParams.has(
        FILTER_OPTION_KEY,
        filterOption?.optionValue
      );
      if (filterAlreadyActive) {
        parsedSearchParams.delete(FILTER_OPTION_KEY, filterOption.optionValue);
      } else {
        parsedSearchParams.append(FILTER_OPTION_KEY, filterOption.optionValue);
      }
      pushUpdatedFilterParams();
    },
    [parsedSearchParams, pushUpdatedFilterParams]
  );

  return {
    filterOptions,
    handleFilterCategory,
    handleFilterOptionClick,
  };
};
